<template>
  <div class="pricing-plans internal-bady" :dir="pageDirection" :lang="pageLanguage">
    <WhatsappChet />
    <NavBar />
    <div id="top"></div>

    <!-- Hero -->
    <div class="px-3 px-md-4 pricing-hero-wrap">
      <div class="container-fluid py-5 bg-light-blue pricing-hero">
        <div class="container pricing-side-pad">
          <div class="row justify-content-center">
            <div class="col-12 col-lg-10 col-xl-8 text-center">
              <h1 class="fw-bold black pricing-hero__title">{{ t("pricingPlansHeroTitle") }}</h1>
              <p class="pricing-hero__subtitle mb-0">{{ t("pricingPlansHeroSubtitle") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plan cards + comparison table. Below 1200px the table comes first (see
         .pricing-cards-compare): the cards no longer fit in one row and would push it down. -->
    <div class="pricing-cards-compare">
    <!-- Plan cards -->
    <div class="container-fluid py-4 py-md-5 pricing-cards-band">
      <div class="container pricing-side-pad">
        <h2 class="visually-hidden-heading">{{ t("pricingPlansCardsTitle") }}</h2>
        <div class="row pricing-cards-row">
          <div v-for="plan in plans" :key="plan.id" class="col-12 col-md-6 col-xl-3 d-flex">
            <article
              class="pricing-card w-100"
              :class="{ 'pricing-card--recommended': plan.recommended }"
              :aria-labelledby="`pricing-card-${plan.id}`"
            >
              <span v-if="plan.recommended" class="pricing-card__badge">
                <i class="fa-solid fa-star" aria-hidden="true"></i>
                {{ t("pricingPlansRecommended") }}
              </span>

              <h3 :id="`pricing-card-${plan.id}`" class="pricing-card__name">{{ plan.name }}</h3>
              <p class="pricing-card__desc">{{ t(plan.descriptionKey) }}</p>

              <div class="pricing-card__price">
                <bdi class="pricing-card__amount">{{ formatPrice(plan.price) }}</bdi>
                <span class="pricing-card__period">{{ t("pricingPlansPerMonth") }}</span>
              </div>
              <p class="pricing-card__usage">
                {{ t("pricingPlansSharedDayCharge", { price: formatPrice(sharedTripDayPrice) }) }}
              </p>
              <p class="pricing-card__trip-terms">
                {{ t("pricingPlansCardTripTerms", { min: formatPrice(minTripCharge), users: includedUsers }) }}
              </p>

              <ul class="pricing-card__features">
                <li>
                  <i class="fa-solid fa-wand-magic-sparkles pricing-card__ico" aria-hidden="true"></i>
                  <span><strong>{{ t("pricingPlansCardAi", { count: plan.aiRequestsPerTripDay }) }}</strong></span>
                </li>
                <li>
                  <i class="fa-brands fa-whatsapp pricing-card__ico" aria-hidden="true"></i>
                  <span><strong>{{ cardWhatsappText(plan) }}</strong></span>
                </li>
                <li>
                  <i class="fa-solid fa-route pricing-card__ico" aria-hidden="true"></i>
                  <span>{{ t("pricingPlansCardItinerary") }}</span>
                </li>
                <li>
                  <i class="fa-solid fa-id-card pricing-card__ico" aria-hidden="true"></i>
                  <span>{{ t("pricingPlansCardBanner") }}</span>
                </li>
                <li :class="{ 'pricing-card__feature--off': !plan.tripDesigns }">
                  <i
                    class="fa-solid pricing-card__ico"
                    :class="plan.tripDesigns ? 'fa-swatchbook' : 'fa-xmark'"
                    aria-hidden="true"
                  ></i>
                  <span>
                    {{ plan.tripDesigns ? t("pricingPlansCardTripDesignsIncluded") : t("pricingPlansCardTripDesignsNotIncluded") }}
                  </span>
                </li>
                <li :class="{ 'pricing-card__feature--off': !plan.whiteLabel }">
                  <i
                    class="fa-solid pricing-card__ico"
                    :class="plan.whiteLabel ? 'fa-palette' : 'fa-xmark'"
                    aria-hidden="true"
                  ></i>
                  <span>
                    {{ plan.whiteLabel ? t("pricingPlansCardWhiteLabelIncluded") : t("pricingPlansCardWhiteLabelNotIncluded") }}
                  </span>
                </li>
              </ul>

              <a
                :href="whatsappLink(t('pricingPlansCtaWhatsappMessage', { plan: plan.name }))"
                target="_blank"
                rel="noopener noreferrer"
                class="btn rounded-pill pricing-card__cta"
                :class="plan.recommended ? 'pricing-btn-primary' : 'pricing-btn-outline'"
              >
                {{ t("pricingPlansCta", { plan: plan.name }) }}
              </a>
            </article>
          </div>
        </div>
        <p class="pricing-cta-note text-center mb-0">
          <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          {{ t("pricingPlansCtaNote") }}
        </p>
      </div>
    </div>

    <!-- Comparison table -->
    <div class="container-fluid bg-light-blue py-4 py-md-5 pricing-compare-band">
      <div class="container pricing-side-pad">
        <section aria-labelledby="pricing-compare-title">
          <h2 id="pricing-compare-title" class="pricing-section-title audience-gradient-title text-center">
            {{ t("pricingPlansCompareTitle") }}
          </h2>
          <div class="pricing-compare__scroll">
            <table class="pricing-compare">
              <thead>
                <tr>
                  <th scope="col" class="pricing-compare__feature pricing-compare__feature--desktop">{{ t("pricingPlansCompareFeature") }}</th>
                  <th
                    v-for="plan in plans"
                    :key="plan.id"
                    scope="col"
                    class="pricing-compare__plan"
                    :class="{ 'pricing-compare__cell--recommended': plan.recommended }"
                  >
                    <span class="pricing-compare__plan-name">{{ plan.name }}</span>
                    <span v-if="plan.recommended" class="pricing-compare__plan-badge">{{ t("pricingPlansRecommended") }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in comparisonRows">
                <!-- Mobile only: the feature name gets its own full-width line, so all
                     four plans fit side by side underneath without sideways scrolling. -->
                <tr :key="row.key + '-label'" class="pricing-compare__label-row">
                  <td :colspan="plans.length">{{ row.label }}</td>
                </tr>
                <tr :key="row.key">
                  <th scope="row" class="pricing-compare__feature pricing-compare__feature--desktop">{{ row.label }}</th>
                  <td
                    v-for="(cell, index) in row.cells"
                    :key="plans[index].id"
                    :class="{ 'pricing-compare__cell--recommended': plans[index].recommended }"
                  >
                    <template v-if="cell.type === 'check'">
                      <span v-if="cell.value" class="pricing-mark pricing-mark--yes">
                        <i class="fa-solid fa-check" aria-hidden="true"></i>
                        <span class="sr-only">{{ t("pricingPlansIncluded") }}</span>
                      </span>
                      <span v-else class="pricing-mark pricing-mark--no">
                        <i class="fa-solid fa-minus" aria-hidden="true"></i>
                        <span class="sr-only">{{ t("pricingPlansNotIncluded") }}</span>
                      </span>
                    </template>
                    <bdi v-else class="pricing-compare__value">{{ cell.value }}</bdi>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="pricing-notes">
            <div class="pricing-note pricing-note--wide">
              <i class="fa-solid fa-users pricing-note__ico" aria-hidden="true"></i>
              <p class="mb-0">
                {{ t("pricingPlansTripRulesNote", {
                  users: includedUsers,
                  price: formatPrice(additionalUserPrice),
                  min: formatPrice(minTripCharge),
                }) }}
              </p>
            </div>
            <div class="pricing-note">
              <i class="fa-brands fa-whatsapp pricing-note__ico" aria-hidden="true"></i>
              <div>
                <h3 class="pricing-note__title">{{ t("pricingPlansWhatsappNoteTitle") }}</h3>
                <p class="mb-2">{{ t("pricingPlansWhatsappNotePerTrip", { plans: perTripPlanNames }) }}</p>
                <p class="mb-0">
                  {{ t("pricingPlansWhatsappNotePerTripDay", {
                    plans: perTripDayPlanNames,
                    plan: recommendedPlan.name,
                    days: example.tripDays,
                    count: recommendedExampleUsage.whatsappPerTraveler,
                  }) }}
                </p>
              </div>
            </div>
            <div class="pricing-note">
              <i class="fa-solid fa-wand-magic-sparkles pricing-note__ico" aria-hidden="true"></i>
              <p class="mb-0">
                {{ t("pricingPlansAiNote", {
                  plan: recommendedPlan.name,
                  days: example.tripDays,
                  count: recommendedExampleUsage.aiRequests,
                }) }}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
    </div>

    <!-- Personal banner vs White Label -->
    <div class="container-fluid py-4 py-md-5">
      <div class="container pricing-side-pad">
        <section aria-labelledby="pricing-branding-title">
          <h2 id="pricing-branding-title" class="pricing-section-title audience-gradient-title text-center">
            {{ t("pricingPlansBrandingTitle") }}
          </h2>
          <p class="pricing-section-sub text-center">{{ t("pricingPlansBrandingSubtitle") }}</p>
          <WhiteLabelDemo :white-label-plan-names="whiteLabelPlanNames" />
        </section>
      </div>
    </div>

    <!-- Usage example -->
    <div class="container-fluid bg-light-blue py-4 py-md-5">
      <div class="container pricing-side-pad">
        <section aria-labelledby="pricing-example-title">
          <h2 id="pricing-example-title" class="pricing-section-title audience-gradient-title text-center">
            {{ t("pricingPlansExampleTitle") }}
          </h2>
          <p class="pricing-section-sub text-center">
            {{ t("pricingPlansExampleSubtitle", { days: example.tripDays, travelers: example.travelers }) }}
          </p>
          <div class="pricing-compare__scroll">
            <table class="pricing-compare pricing-compare--example">
              <thead>
                <tr>
                  <th scope="col" class="pricing-compare__feature pricing-compare__feature--desktop"></th>
                  <th
                    v-for="plan in plans"
                    :key="plan.id"
                    scope="col"
                    class="pricing-compare__plan"
                    :class="{ 'pricing-compare__cell--recommended': plan.recommended }"
                  >
                    <span class="pricing-compare__plan-name">{{ plan.name }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in exampleRows">
                <tr :key="row.key + '-label'" class="pricing-compare__label-row">
                  <td :colspan="plans.length">{{ row.label }}</td>
                </tr>
                <tr :key="row.key">
                  <th scope="row" class="pricing-compare__feature pricing-compare__feature--desktop">{{ row.label }}</th>
                  <td
                    v-for="(value, index) in row.values"
                    :key="plans[index].id"
                    :class="{ 'pricing-compare__cell--recommended': plans[index].recommended }"
                  >
                    <bdi class="pricing-compare__value">{{ value }}</bdi>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>
          <p class="pricing-example__note">
            <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
            {{ t("pricingPlansExampleNote", {
              days: example.tripDays,
              price: formatPrice(sharedTripDayPrice),
              total: formatPrice(recommendedExampleUsage.tripCharge),
              users: includedUsers,
            }) }}
          </p>

          <h3 id="pricing-trip-price-title" class="pricing-trip-price__title text-center">
            {{ t("pricingPlansTripPriceTitle") }}
          </h3>
          <p class="pricing-trip-price__sub text-center">{{ t("pricingPlansTripPriceSubtitle") }}</p>
          <div class="pricing-trip-price">
            <table class="pricing-trip-price__table" aria-labelledby="pricing-trip-price-title">
              <thead>
                <tr>
                  <th scope="col">{{ t("pricingPlansTripPriceColTrip") }}</th>
                  <th scope="col" class="d-none d-md-table-cell">{{ t("pricingPlansTripPriceColCalc") }}</th>
                  <th scope="col">{{ t("pricingPlansTripPriceColTotal") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tripPriceRows" :key="row.key">
                  <th scope="row">
                    {{ row.trip }}
                    <!-- Phones: the calculation moves under the trip label instead of its own column. -->
                    <span class="pricing-trip-price__calc pricing-trip-price__calc--inline d-md-none">
                      <template v-for="(part, index) in row.calculation">
                        <span v-if="index > 0" :key="`op-${index}`"> + </span>
                        <bdi :key="`part-${index}`">{{ part }}</bdi>
                      </template>
                    </span>
                  </th>
                  <td class="pricing-trip-price__calc d-none d-md-table-cell">
                    <!-- Each term is isolated so RTL bidi can't merge "₪5 + 2" into one LTR run. -->
                    <template v-for="(part, index) in row.calculation">
                      <span v-if="index > 0" :key="`op-${index}`"> + </span>
                      <bdi :key="`part-${index}`">{{ part }}</bdi>
                    </template>
                  </td>
                  <td><bdi class="pricing-trip-price__total">{{ row.total }}</bdi></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>

    <!-- FAQ -->
    <div class="container-fluid py-4 py-md-5">
      <div class="container pricing-side-pad">
        <section aria-labelledby="pricing-faq-title" class="pricing-faq">
          <h2 id="pricing-faq-title" class="pricing-section-title audience-gradient-title text-center">
            {{ t("pricingPlansFaqTitle") }}
          </h2>
          <details v-for="faq in faqs" :key="faq.key" class="pricing-faq__item">
            <summary class="pricing-faq__question">
              <span>{{ faq.question }}</span>
              <i class="fa-solid fa-chevron-down pricing-faq__chevron" aria-hidden="true"></i>
            </summary>
            <p class="pricing-faq__answer mb-0">{{ faq.answer }}</p>
          </details>
        </section>
      </div>
    </div>

    <!-- Bottom CTA -->
    <div class="container-fluid bg-light-blue py-5">
      <div class="container pricing-side-pad text-center">
        <h2 class="pricing-bottom-cta__title black mb-3">{{ t("pricingPlansBottomCtaTitle") }}</h2>
        <p class="pricing-bottom-cta__text text-muted mb-4">{{ t("pricingPlansBottomCtaText") }}</p>
        <a
          :href="whatsappLink(t('pricingPlansBottomCtaMessage'))"
          target="_blank"
          rel="noopener noreferrer"
          class="btn rounded-pill pricing-btn-primary pricing-bottom-cta__btn"
        >
          <i class="fa-brands fa-whatsapp" aria-hidden="true"></i>
          {{ t("pricingPlansBottomCtaButton") }}
        </a>
      </div>
    </div>

    <PageFooterNew />
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import PageFooterNew from "@/components/PageFooterNew.vue";
import WhatsappChet from "@/components/WhatsappChet";
import WhiteLabelDemo from "@/components/WhiteLabelDemo.vue";
import { translationMixin } from "@/mixins/translationMixin";
import {
  CURRENCY_ILS,
  CURRENCY_USD,
  PRICING_CURRENCIES,
  WHATSAPP_BASIS_PER_TRIP,
  WHATSAPP_BASIS_PER_TRIP_DAY,
  PRICING_PLANS,
  PRICING_USAGE_EXAMPLE,
  TRIP_PRICE_EXAMPLES,
  INCLUDED_USERS_PER_TRIP,
  PRICING_CONTACT_WHATSAPP_NUMBER,
  calculateTripPrice,
  calculateTripUsage,
} from "@/common/pricingPlans";

// Presentation-only page: CTAs open a WhatsApp chat, nothing here buys or changes a plan.
export default {
  name: "PricingPlans",
  mixins: [translationMixin],
  components: {
    NavBar,
    PageFooterNew,
    WhatsappChet,
    WhiteLabelDemo,
  },
  data() {
    return {
      includedUsers: INCLUDED_USERS_PER_TRIP,
      example: PRICING_USAGE_EXAMPLE,
    };
  },
  computed: {
    pageLanguage() {
      return this.getCurrentLanguage === "he" ? "he" : "en";
    },
    pageDirection() {
      return this.getCurrentLanguage === "he" ? "rtl" : "ltr";
    },
    // Hebrew shows ILS prices, English shows USD prices.
    currency() {
      return this.getCurrentLanguage === "he" ? CURRENCY_ILS : CURRENCY_USD;
    },
    rates() {
      return PRICING_CURRENCIES[this.currency];
    },
    sharedTripDayPrice() {
      return this.rates.sharedTripDay;
    },
    minTripCharge() {
      return this.rates.minTripCharge;
    },
    additionalUserPrice() {
      return this.rates.additionalUser;
    },
    plans() {
      // Card descriptions are keyed by plan name: pricingPlansDescFree, pricingPlansDescPro, ...
      return PRICING_PLANS.map((plan) => ({
        ...plan,
        descriptionKey: `pricingPlansDesc${plan.name}`,
        price: plan.monthlyPrice[this.currency],
        exampleUsage: calculateTripUsage(plan, this.example.tripDays, this.example.travelers, this.currency),
      }));
    },
    recommendedPlan() {
      return this.plans.find((plan) => plan.recommended) || this.plans[this.plans.length - 1];
    },
    recommendedExampleUsage() {
      return this.recommendedPlan.exampleUsage;
    },
    perTripPlanNames() {
      return this.joinPlanNames((plan) => plan.whatsappBasis === WHATSAPP_BASIS_PER_TRIP);
    },
    perTripDayPlanNames() {
      return this.joinPlanNames((plan) => plan.whatsappBasis === WHATSAPP_BASIS_PER_TRIP_DAY);
    },
    whiteLabelPlanNames() {
      return this.joinPlanNames((plan) => plan.whiteLabel);
    },
    freePlanNames() {
      return this.joinPlanNames((plan) => plan.price === 0);
    },
    comparisonRows() {
      const textRow = (key, label, valueOf) => ({
        key,
        label,
        cells: this.plans.map((plan) => ({ type: "text", value: valueOf(plan) })),
      });
      const checkRow = (key, label, valueOf) => ({
        key,
        label,
        cells: this.plans.map((plan) => ({ type: "check", value: valueOf(plan) })),
      });

      return [
        textRow("monthly", this.t("pricingPlansRowMonthly"), (plan) => this.formatPrice(plan.price)),
        textRow("sharedDay", this.t("pricingPlansRowSharedDay"), () => this.formatPrice(this.sharedTripDayPrice)),
        textRow("minTrip", this.t("pricingPlansRowMinTrip"), () => this.formatPrice(this.minTripCharge)),
        textRow("includedUsers", this.t("pricingPlansRowIncludedUsers"), () => this.includedUsers),
        textRow("additionalUser", this.t("pricingPlansRowAdditionalUser"), () => this.formatPrice(this.additionalUserPrice)),
        checkRow("itinerary", this.t("pricingPlansRowItinerary"), () => true),
        checkRow("banner", this.t("pricingPlansRowBanner"), () => true),
        textRow("ai", this.t("pricingPlansRowAi"), (plan) => plan.aiRequestsPerTripDay),
        textRow("whatsapp", this.t("pricingPlansRowWhatsapp"), (plan) => this.tableWhatsappText(plan)),
        checkRow("tripDesigns", this.t("pricingPlansRowTripDesigns"), (plan) => plan.tripDesigns),
        checkRow("whiteLabel", this.t("pricingPlansRowWhiteLabel"), (plan) => plan.whiteLabel),
      ];
    },
    exampleRows() {
      const row = (key, label, valueOf) => ({
        key,
        label,
        values: this.plans.map((plan) => valueOf(plan.exampleUsage)),
      });

      return [
        row("tripCharge", this.t("pricingPlansExampleRowTripCharge"), (usage) => this.formatPrice(usage.tripCharge)),
        row("ai", this.t("pricingPlansExampleRowAi"), (usage) => usage.aiRequests),
        row("whatsappPerTraveler", this.t("pricingPlansExampleRowWhatsappPerTraveler"), (usage) => usage.whatsappPerTraveler),
        row("whatsappTotal", this.t("pricingPlansExampleRowWhatsappTotal"), (usage) => usage.whatsappTotal),
      ];
    },
    tripPriceRows() {
      return TRIP_PRICE_EXAMPLES.map(({ tripDays, users }) => {
        const price = calculateTripPrice(tripDays, users, this.currency);
        const days = tripDays === 1
          ? this.t("pricingPlansTripDaysOne")
          : this.t("pricingPlansTripDaysMany", { days: tripDays });

        return {
          key: `${tripDays}-${users}`,
          trip: `${days} · ${this.t("pricingPlansTripUsers", { users })}`,
          calculation: this.tripPriceCalculation(tripDays, price),
          total: this.formatPrice(price.total),
        };
      });
    },
    // First example with users beyond the included ones, used to illustrate the extra-user charge.
    extraUsersExample() {
      const { tripDays, users } = TRIP_PRICE_EXAMPLES.find((e) => e.users > this.includedUsers);
      return { tripDays, users, total: calculateTripPrice(tripDays, users, this.currency).total };
    },
    faqs() {
      const usage = this.recommendedExampleUsage;
      const days = this.example.tripDays;

      return [
        {
          key: "commitment",
          question: this.t("pricingPlansFaq1Q"),
          answer: this.t("pricingPlansFaq1A", { freePlan: this.freePlanNames }),
        },
        {
          key: "switchPlans",
          question: this.t("pricingPlansFaqSwitchQ"),
          answer: this.t("pricingPlansFaqSwitchA"),
        },
        {
          key: "tripDays",
          question: this.t("pricingPlansFaq2Q"),
          answer: this.t("pricingPlansFaq2A", {
            price: this.formatPrice(this.sharedTripDayPrice),
            min: this.formatPrice(this.minTripCharge),
            users: this.includedUsers,
            userPrice: this.formatPrice(this.additionalUserPrice),
            days: this.extraUsersExample.tripDays,
            extraUsersExample: this.extraUsersExample.users,
            total: this.formatPrice(this.extraUsersExample.total),
          }),
        },
        {
          key: "ai",
          question: this.t("pricingPlansFaq3Q"),
          answer: this.t("pricingPlansFaq3A", { plan: this.recommendedPlan.name, days, count: usage.aiRequests }),
        },
        {
          key: "whatsapp",
          question: this.t("pricingPlansFaq4Q"),
          answer: this.t("pricingPlansFaq4A", {
            perTripPlans: this.perTripPlanNames,
            perDayPlans: this.perTripDayPlanNames,
            plan: this.recommendedPlan.name,
            days,
            count: usage.whatsappPerTraveler,
          }),
        },
        {
          key: "branding",
          question: this.t("pricingPlansFaq5Q"),
          answer: this.t("pricingPlansFaq5A", { plan: this.whiteLabelPlanNames }),
        },
      ];
    },
  },
  methods: {
    formatPrice(amount) {
      return `${this.rates.symbol}${amount}`;
    },
    tripPriceCalculation(tripDays, price) {
      const parts = [
        tripDays * this.sharedTripDayPrice < this.minTripCharge
          ? this.t("pricingPlansCalcMinimum", { min: this.formatPrice(this.minTripCharge) })
          : this.t("pricingPlansCalcDays", { days: tripDays, price: this.formatPrice(this.sharedTripDayPrice) }),
      ];
      if (price.additionalUsers === 1) {
        parts.push(this.t("pricingPlansCalcExtraUserOne", { price: this.formatPrice(this.additionalUserPrice) }));
      } else if (price.additionalUsers > 1) {
        parts.push(this.t("pricingPlansCalcExtraUsers", {
          count: price.additionalUsers,
          price: this.formatPrice(this.additionalUserPrice),
        }));
      }
      return parts;
    },
    joinPlanNames(predicate) {
      return this.plans.filter(predicate).map((plan) => plan.name).join(this.t("pricingPlansAnd"));
    },
    isPerTripDay(plan) {
      return plan.whatsappBasis === WHATSAPP_BASIS_PER_TRIP_DAY;
    },
    cardWhatsappText(plan) {
      const key = this.isPerTripDay(plan) ? "pricingPlansCardWhatsappPerTripDay" : "pricingPlansCardWhatsappPerTrip";
      return this.t(key, { count: plan.whatsappPerTraveler });
    },
    tableWhatsappText(plan) {
      const key = this.isPerTripDay(plan) ? "pricingPlansWhatsappPerTripDay" : "pricingPlansWhatsappPerTrip";
      return this.t(key, { count: plan.whatsappPerTraveler });
    },
    whatsappLink(message) {
      return `https://wa.me/${PRICING_CONTACT_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    },
  },
  metaInfo() {
    return {
      title: this.t("pricingPlansMetaTitle"),
      meta: [
        { name: "description", content: this.t("pricingPlansMetaDescription") },
        // The plans are not live yet; keep the page out of search results until launch.
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  },
};
</script>

<style scoped>
/* Root font-size in this app is 10px, so 1.6rem = 16px. Palette matches PaymentHowItWorks. */
.internal-bady {
  line-height: 1.5;
}

.pricing-plans {
  --pp-cyan: #34d0b6;
  --pp-blue: #07a0c3;
  --pp-black: #1e1e1e;
  --pp-muted: rgba(33, 37, 41, 0.72);
  --pp-line: rgba(7, 160, 195, 0.14);
  --pp-tint: rgba(7, 160, 195, 0.06);
  background: #fff;
  min-height: 100vh;
  color: var(--pp-black);
  /* Global styles right-align text for Hebrew; follow the page direction instead. */
  text-align: start;
}

.black {
  color: var(--pp-black);
}

.bg-light-blue {
  background-color: #e6f5f9 !important;
}

.visually-hidden-heading {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

/* ---------- Hero ---------- */
.pricing-hero-wrap {
  padding-top: 98px;
  padding-bottom: 8px;
}

.pricing-hero {
  border-radius: 32px;
}

.pricing-hero__title {
  font-size: calc(3.6rem + 1.4vw);
  line-height: 1.25;
  font-weight: 800;
  margin-bottom: 1.6rem;
}

.pricing-hero__subtitle {
  font-size: 2rem;
  line-height: 1.7;
  color: var(--pp-muted);
}

/* ---------- Cards ---------- */
.pricing-cards-row {
  row-gap: 2.4rem;
  padding-top: 1.6rem;
}

.pricing-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 3.2rem 2.4rem 2.4rem;
  background: #fff;
  border: 1px solid var(--pp-line);
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(7, 160, 195, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(7, 160, 195, 0.18);
}

.pricing-card--recommended {
  border: 2px solid var(--pp-blue);
  background: linear-gradient(180deg, rgba(230, 245, 249, 0.7) 0%, #fff 38%);
  box-shadow: 0 10px 32px rgba(7, 160, 195, 0.22);
}

.pricing-card__badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 1.6rem;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  background: linear-gradient(45deg, var(--pp-cyan), var(--pp-blue));
  border-radius: 999px;
  box-shadow: 0 2px 10px rgba(7, 160, 195, 0.35);
}

.pricing-card__name {
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 0.6rem;
}

.pricing-card--recommended .pricing-card__name {
  color: var(--pp-blue);
}

.pricing-card__desc {
  font-size: 1.6rem;
  line-height: 1.5;
  color: var(--pp-muted);
  min-height: 4.8rem;
  margin-bottom: 1.6rem;
}

.pricing-card__price {
  display: flex;
  align-items: baseline;
  gap: 0.8rem;
}

.pricing-card__amount {
  font-size: 4.8rem;
  font-weight: 800;
  line-height: 1;
  unicode-bidi: isolate;
}

.pricing-card__period {
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--pp-muted);
}

.pricing-card__usage {
  display: inline-block;
  align-self: flex-start;
  margin: 1.2rem 0 2rem;
  padding: 0.4rem 1.2rem;
  font-size: 1.4rem;
  font-weight: 600;
  color: #0b6f86;
  background: var(--pp-tint);
  border-radius: 8px;
}

.pricing-card__trip-terms {
  margin: -1.2rem 0 2rem;
  font-size: 1.3rem;
  color: var(--pp-muted);
}

.pricing-card__features {
  list-style: none;
  margin: 0 0 2.4rem;
  padding: 2rem 0 0;
  border-top: 1px solid var(--pp-line);
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  flex-grow: 1;
}

.pricing-card__features li {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  font-size: 1.5rem;
  line-height: 1.45;
}

.pricing-card__ico {
  flex-shrink: 0;
  width: 2rem;
  margin-top: 0.2rem;
  text-align: center;
  font-size: 1.6rem;
  color: var(--pp-blue);
}

.pricing-card__features .fa-whatsapp {
  color: #25d366;
  font-size: 1.8rem;
}

.pricing-card__feature--off {
  color: rgba(33, 37, 41, 0.45);
}

.pricing-card__feature--off .pricing-card__ico {
  color: rgba(33, 37, 41, 0.35);
}

.pricing-card__cta {
  width: 100%;
  padding: 1.2rem 1.6rem;
  font-size: 1.6rem;
  font-weight: 700;
}

.pricing-btn-primary {
  background: linear-gradient(45deg, var(--pp-cyan), var(--pp-blue));
  color: #fff !important;
  border: 0;
}

.pricing-btn-primary:hover {
  background: linear-gradient(45deg, #39e1c5, #08add3);
  color: #fff !important;
}

.pricing-btn-outline {
  background: #fff;
  color: var(--pp-blue) !important;
  border: 2px solid var(--pp-blue);
}

.pricing-btn-outline:hover {
  background: var(--pp-tint);
}

.pricing-cta-note {
  margin-top: 2.4rem;
  font-size: 1.4rem;
  color: var(--pp-muted);
}

/* ---------- Section headings ---------- */
.audience-gradient-title {
  background: linear-gradient(135deg, var(--pp-cyan), var(--pp-blue));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent !important;
}

.pricing-section-title {
  font-size: calc(2.6rem + 0.55vw);
  font-weight: 800;
  margin-bottom: 1.2rem;
}

.pricing-section-sub {
  font-size: 1.8rem;
  color: var(--pp-muted);
  margin-bottom: 2.8rem;
}

/* ---------- Comparison / example tables ---------- */
.pricing-compare__scroll {
  /* Containing block for the absolutely positioned .sr-only labels, so they can't widen the page. */
  position: relative;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  border: 1px solid var(--pp-line);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(7, 160, 195, 0.1);
  margin-top: 2rem;
}

.pricing-compare {
  width: 100%;
  min-width: 68rem;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1.6rem;
}

.pricing-compare th,
.pricing-compare td {
  padding: 1.4rem 1.6rem;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid var(--pp-line);
}

.pricing-compare tbody tr:last-child th,
.pricing-compare tbody tr:last-child td {
  border-bottom: 0;
}

.pricing-compare tbody tr:hover th,
.pricing-compare tbody tr:hover td {
  background-color: rgba(230, 245, 249, 0.55);
}

/* First column stays visible while the plans scroll sideways on narrow screens. */
.pricing-compare__feature {
  position: sticky;
  inset-inline-start: 0;
  z-index: 1;
  text-align: start !important;
  font-weight: 600;
  background: #fff;
  min-width: 18rem;
  box-shadow: inset -1px 0 0 var(--pp-line);
}

[dir="rtl"] .pricing-compare__feature {
  box-shadow: inset 1px 0 0 var(--pp-line);
}

.pricing-compare thead th {
  background: #f6fbfd;
  font-size: 1.4rem;
  color: var(--pp-muted);
}

.pricing-compare__plan-name {
  display: block;
  font-size: 1.9rem;
  font-weight: 800;
  color: var(--pp-black);
}

.pricing-compare__plan-badge {
  display: inline-block;
  margin-top: 0.4rem;
  padding: 0.1rem 0.9rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  background: linear-gradient(45deg, var(--pp-cyan), var(--pp-blue));
  border-radius: 999px;
}

.pricing-compare__cell--recommended,
.pricing-compare thead th.pricing-compare__cell--recommended {
  background-color: rgba(7, 160, 195, 0.07);
}

.pricing-compare__value {
  font-weight: 600;
  unicode-bidi: isolate;
}

.pricing-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  font-size: 1.2rem;
}

.pricing-mark--yes {
  color: #fff;
  background: linear-gradient(145deg, var(--pp-cyan), var(--pp-blue));
  box-shadow: 0 2px 6px rgba(7, 160, 195, 0.3);
}

.pricing-mark--no {
  color: rgba(33, 37, 41, 0.35);
  background: rgba(33, 37, 41, 0.06);
}

.pricing-compare--example td .pricing-compare__value {
  font-size: 1.8rem;
}

/* Mobile-only feature-name rows (see the max-width: 767px block). */
.pricing-compare__label-row {
  display: none;
}

/* ---------- Notes under the comparison table ---------- */
.pricing-notes {
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 1.6rem;
  margin-top: 2.4rem;
}

@media (min-width: 992px) {
  .pricing-notes {
    grid-template-columns: 3fr 2fr;
  }
}

.pricing-note {
  display: flex;
  gap: 1.4rem;
  align-items: flex-start;
  padding: 2rem 2.2rem;
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--pp-line);
  font-size: 1.6rem;
  line-height: 1.65;
  color: rgba(33, 37, 41, 0.88);
}

.pricing-note--wide {
  grid-column: 1 / -1;
  align-items: center;
  font-weight: 600;
  border-color: rgba(7, 160, 195, 0.35);
}

.pricing-note__ico {
  flex-shrink: 0;
  font-size: 2.4rem;
  color: var(--pp-blue);
}

.pricing-note .fa-whatsapp {
  color: #25d366;
}

.pricing-note__title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
}

/* ---------- Usage example ---------- */
.pricing-example__note {
  margin: 2rem 0 0;
  font-size: 1.6rem;
  font-weight: 600;
  color: rgba(33, 37, 41, 0.85);
  text-align: center;
}

.pricing-example__note i {
  color: var(--pp-blue);
}

/* Trip price examples: same for every plan, so a compact 3-column table. */
.pricing-trip-price__title {
  margin-top: 4rem;
  font-size: 2.2rem;
  font-weight: 800;
}

.pricing-trip-price__sub {
  font-size: 1.6rem;
  color: var(--pp-muted);
  margin-bottom: 2rem;
}

.pricing-trip-price {
  max-width: 72rem;
  margin-inline: auto;
  background: #fff;
  border: 1px solid var(--pp-line);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(7, 160, 195, 0.1);
  overflow: hidden;
}

.pricing-trip-price__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.6rem;
}

.pricing-trip-price__table th,
.pricing-trip-price__table td {
  padding: 1.2rem 1.6rem;
  text-align: start;
  vertical-align: middle;
  border-bottom: 1px solid var(--pp-line);
}

.pricing-trip-price__table tbody tr:last-child th,
.pricing-trip-price__table tbody tr:last-child td {
  border-bottom: 0;
}

.pricing-trip-price__table thead th {
  background: #f6fbfd;
  font-size: 1.4rem;
  color: var(--pp-muted);
}

.pricing-trip-price__table tbody th {
  font-weight: 600;
}

.pricing-trip-price__calc {
  color: var(--pp-muted);
}

.pricing-trip-price__calc--inline {
  display: block;
  margin-top: 0.4rem;
  font-weight: 400;
}

/* Terms wrap between each other, never inside a term or a price. */
.pricing-trip-price__calc bdi,
.pricing-trip-price__total {
  white-space: nowrap;
}

.pricing-trip-price__total {
  font-size: 1.8rem;
  font-weight: 800;
  unicode-bidi: isolate;
}

/* ---------- FAQ ---------- */
.pricing-faq {
  max-width: 84rem;
  margin-inline: auto;
}

.pricing-faq__item {
  background: #fff;
  border: 1px solid var(--pp-line);
  border-radius: 14px;
  margin-top: 1.2rem;
  box-shadow: 0 2px 10px rgba(7, 160, 195, 0.06);
}

.pricing-faq__question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
  padding: 1.8rem 2.2rem;
  font-size: 1.8rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}

.pricing-faq__question::-webkit-details-marker {
  display: none;
}

.pricing-faq__chevron {
  flex-shrink: 0;
  font-size: 1.4rem;
  color: var(--pp-blue);
  transition: transform 0.2s ease;
}

.pricing-faq__item[open] .pricing-faq__chevron {
  transform: rotate(180deg);
}

.pricing-faq__answer {
  padding: 0 2.2rem 2rem;
  font-size: 1.6rem;
  line-height: 1.7;
  color: var(--pp-muted);
  /* Answers may hold "\n\n" paragraph breaks. */
  white-space: pre-line;
}

/* ---------- Bottom CTA ---------- */
.pricing-bottom-cta__title {
  font-size: calc(2.8rem + 0.55vw);
  font-weight: 800;
}

.pricing-bottom-cta__text {
  font-size: 2rem;
}

.pricing-bottom-cta__btn {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 3.6rem;
  font-size: 2rem;
  font-weight: 700;
}

/* ---------- Responsive ---------- */
@media (min-width: 1200px) {
  .pricing-card {
    padding-inline: 2rem;
  }

  /* Four cards in one row: reserve 3 lines so the prices line up across cards. */
  .pricing-card__desc {
    min-height: 7.2rem;
  }
}

/* Below 1200px the four plan cards no longer fit in one row, so the comparison
   table (all four plans at once) comes before them. Colours are swapped so the
   page keeps alternating white / light-blue bands (bg-light-blue is !important). */
@media (max-width: 1199.98px) {
  .pricing-cards-compare {
    display: flex;
    flex-direction: column;
  }

  .pricing-compare-band {
    order: -1;
    background-color: #fff !important;
  }

  .pricing-cards-band {
    background-color: #e6f5f9;
  }
}

@media (max-width: 991.98px) {
  .pricing-side-pad {
    padding-left: 1.6rem !important;
    padding-right: 1.6rem !important;
  }
}

@media (max-width: 767px) {
  .pricing-hero-wrap {
    padding-top: 74px;
  }

  .pricing-hero {
    border-radius: 20px;
  }

  .pricing-hero__title {
    font-size: calc(2.8rem + 1.2vw);
  }

  .pricing-hero__subtitle {
    font-size: 1.7rem;
  }

  .pricing-card:hover {
    transform: none;
  }

  .pricing-card__desc {
    min-height: 0;
  }

  /* All four plans side by side on a phone: no feature column, no sideways
     scrolling. Each feature name sits on its own line above its four values. */
  .pricing-compare {
    min-width: 0;
    table-layout: fixed;
    font-size: 1.4rem;
  }

  .pricing-compare th,
  .pricing-compare td {
    padding: 1rem 0.4rem;
    overflow-wrap: anywhere;
  }

  .pricing-compare__feature--desktop {
    display: none;
  }

  .pricing-compare__label-row {
    display: table-row;
  }

  .pricing-compare__label-row td {
    padding: 1rem 1.2rem 0.2rem;
    text-align: start;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--pp-muted);
    background: #f6fbfd;
    border-bottom: 0;
  }

  .pricing-compare tbody tr:hover th,
  .pricing-compare tbody tr:hover td {
    background-color: transparent;
  }

  .pricing-compare tbody tr.pricing-compare__label-row:hover td {
    background: #f6fbfd;
  }

  .pricing-compare__plan-name {
    font-size: 1.5rem;
  }

  .pricing-compare__plan-badge {
    font-size: 1rem;
    padding: 0.1rem 0.5rem;
    white-space: normal;
  }

  .pricing-mark {
    width: 2.2rem;
    height: 2.2rem;
  }

  .pricing-compare--example td .pricing-compare__value {
    font-size: 1.5rem;
  }

  .pricing-trip-price__table {
    font-size: 1.4rem;
  }

  .pricing-trip-price__table th,
  .pricing-trip-price__table td {
    padding: 1rem 0.8rem;
  }

  .pricing-bottom-cta__btn {
    font-size: 1.8rem;
    padding: 1.2rem 2.8rem;
  }
}
</style>
